import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';

const configuredAuthDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;

/**
 * When VITE_FIREBASE_AUTH_DOMAIN_MODE=proxy, the Firebase auth handler is served
 * from our own origin through the /__/ reverse proxy, which is what makes
 * sign-in work in Safari (see backend/docs/AUTH_SAFARI_SAME_ORIGIN_HANDLER.md).
 *
 * The https guard is not optional. The SDK builds the handler URL as
 * `https://${authDomain}/__/auth/handler` with the scheme HARDCODED
 * (@firebase/auth, getHandlerBase). Pointing authDomain at a plain-http origin
 * therefore produces a URL to a port that speaks http, and the request hangs
 * rather than fails — which stalls _initializeWithPersistence, so
 * onAuthStateChanged never fires and the app never leaves its loading state.
 * A hung sign-in is bad; a hung app that never renders the login page is worse.
 *
 * Consequence: a plain `npm run dev` on http://localhost keeps the default
 * authDomain and still reproduces the original Safari bug. To exercise the fix
 * locally, run the dev server over https and add the matching
 * `https://localhost:5173/__/auth/handler` redirect URI in the Google OAuth client.
 */
const resolveAuthDomain = (): string => {
  if (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_MODE !== 'proxy') {
    return configuredAuthDomain;
  }
  if (typeof window === 'undefined' || !window.location?.host) {
    return configuredAuthDomain;
  }
  if (window.location.protocol !== 'https:') {
    console.warn(
      '[firebase] Auth handler proxy mode requested but this origin is not https. ' +
      `Falling back to ${configuredAuthDomain}. Serve the app over https to use the proxy.`
    );
    return configuredAuthDomain;
  }
  return window.location.host;
};

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: resolveAuthDomain(),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

console.log('Firebase Config:', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  authDomainMode: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_MODE || 'default',
});

const app = initializeApp(firebaseConfig);

// getAuth already initializes with the persistence hierarchy
// [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence]
// and browserPopupRedirectResolver. Do not replace it with a hand-rolled
// initializeAuth — an earlier revision did and silently dropped the session
// persistence fallback.
export const auth = getAuth(app);
export default app;
